import { Suspense, useState } from 'react';
import { Context as ResponsiveContext } from 'react-responsive';
import { hydrate, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Provider as ReakitProvider } from 'reakit';

import { I18nProvider } from '@cx-modules/i18n-provider';
import { DefaultLoadingFallback } from '@cx-modules/react-utils';
import { type Betslip } from '@cx-modules/types';

import { SubeventHeader } from './SubeventHeader';
import type { SubeventHeaderIndexProps } from './types';
import { breakpoints } from './utils';

// The @cx-modules/hooks barrel (imported for useAfterFirstRender) transitively pulls in
// use-register-oc-betslip-callbacks, which references window.ocBetslip. Declare the global so the
// bundled .d.ts type-check passes — mirrors most-popular-bets/module/index.tsx. dts-bundle-generator
// does not emit this augmentation into dist/index.d.ts, so it stays contained to the build.
declare global {
  interface Window {
    ocBetslip?: Betslip;
  }
}

const createQueryClient = (nocache?: boolean) =>
  new QueryClient({
    defaultOptions: {
      queries: {
        gcTime: nocache ? 0 : undefined,
        refetchOnMount: nocache ? true : false,
        staleTime: nocache ? 0 : Infinity,
      },
    },
  });

function SubeventHeaderWithQuery({ dehydratedState, deviceType, nocache, ...props }: SubeventHeaderIndexProps) {
  const [queryClient] = useState(() => {
    const client = createQueryClient(nocache);
    hydrate(client, dehydratedState);
    return client;
  });

  const isoRender = (
    <ReakitProvider>
      <QueryClientProvider client={queryClient}>
        <I18nProvider i18nInstance={props.i18n}>
          <Suspense fallback={<DefaultLoadingFallback />}>
            <SubeventHeader {...props} />
          </Suspense>
        </I18nProvider>
      </QueryClientProvider>
    </ReakitProvider>
  );

  if (typeof window !== 'undefined') {
    return isoRender;
  }

  const deviceWidth =
    deviceType === 'MOBILE' ? breakpoints.phoneSm : deviceType === 'TABLET' ? breakpoints.tablet : breakpoints.desktop;

  return <ResponsiveContext.Provider value={{ width: deviceWidth }}>{isoRender}</ResponsiveContext.Provider>;
}

// The props type is part of the public API — every consumer wraps this component and types its
// own props with it. Re-exported explicitly: the dist `.d.ts` dts-bundle-generator used to emit
// hoisted it automatically, and since NGF-6661 this file IS the package's type surface.
export type { SubeventHeaderIndexProps } from './types';

export { SubeventHeaderWithQuery };

export { loadDataForServer } from './loadData';
